import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, useAuth } from "../App";
import UsdHint from "../components/UsdHint";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Separator } from "../components/ui/separator";
import {
  Star,
  MapPin,
  Users,
  Fuel,
  Settings2,
  Calendar,
  Shield,
  Zap,
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  Car,
} from "lucide-react";
import TripScheduler from "../components/TripScheduler";
import { es } from "date-fns/locale";
import useSeo from "../hooks/useSeo";

export default function CarDetailsPage() {
  const { t } = useTranslation();
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [vehicleReviews, setVehicleReviews] = useState({ reviews: [], total: 0, average: null, breakdown: {} });
  const [hostReviews, setHostReviews] = useState({ reviews: [], total: 0, average: null, breakdown: {} });

  const carName = vehicle
    ? `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim()
    : "Renta de auto";
  const carCity = vehicle?.location_city || vehicle?.location_address || "Tijuana";
  useSeo({
    title: vehicle
      ? `Renta de auto ${carName} en ${carCity} · Rivones`
      : "Renta de auto · Rivones",
    description: vehicle
      ? `Renta ${carName} en ${carCity} por $${Math.round(vehicle.daily_rate || 0)}/día. Renta de autos entre particulares en México — reserva en Rivones.`
      : "Renta de autos entre particulares en México. Reserva en Rivones.",
    keywords: `renta de ${(vehicle?.make || "autos").toLowerCase()}, renta de autos en ${carCity}, renta de autos, ${carName.toLowerCase()}`,
    canonical: `https://rivones.com/cars/${vehicleId}`,
    image: vehicle?.photos?.[vehicle?.cover_photo_index || 0] || vehicle?.photos?.[0] || undefined,
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const vehResp = await api.get(`/vehicles/${vehicleId}`);
        setVehicle(vehResp.data);
        const [vr, hr] = await Promise.all([
          api.get(`/reviews/vehicle/${vehicleId}`),
          vehResp.data?.host?.user_id
            ? api.get(`/reviews/user/${vehResp.data.host.user_id}`)
            : Promise.resolve({ data: { reviews: [], total: 0, average: null, breakdown: {} } }),
        ]);
        setVehicleReviews(vr.data);
        setHostReviews(hr.data);
      } catch (error) {
        console.error("Error fetching vehicle:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [vehicleId]);

  const [tripSelection, setTripSelection] = useState(null);

  const handleBookNow = () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: `/checkout/${vehicleId}` } });
      return;
    }
    const params = new URLSearchParams();
    if (tripSelection?.pickup) {
      params.set("pickup", tripSelection.pickup.toISOString());
    }
    if (tripSelection?.returnDate) {
      params.set("return", tripSelection.returnDate.toISOString());
    }
    const qs = params.toString();
    navigate(`/checkout/${vehicleId}${qs ? "?" + qs : ""}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Car className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t("carDetails.notFound")}</h2>
        <Link to="/search">
          <Button className="btn-primary">{t("carDetails.searchCars")}</Button>
        </Link>
      </div>
    );
  }

  const photos = vehicle.photos?.length > 0
    ? vehicle.photos
    : ["https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800"];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % photos.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  return (
    <div className="min-h-screen bg-sand pb-24 md:pb-8" data-testid="car-details-page">
      <div className="container-main py-6">
        {/* Back Button */}
        <Link
          to="/search"
          className="inline-flex items-center text-gray-600 hover:text-terracotta mb-6"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t("carDetails.backToResults")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images & Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="relative rounded-2xl overflow-hidden bg-white">
              <img
                src={photos[currentImageIndex]}
                alt={`${vehicle.make} ${vehicle.model}`}
                className="w-full aspect-[16/9] object-cover"
              />
              
              {photos.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  
                  {/* Thumbnails */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {photos.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentImageIndex ? "bg-terracotta" : "bg-white/60"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                {vehicle.instant_book && (
                  <Badge className="bg-marigold text-agave font-semibold">
                    <Zap className="w-3 h-3 mr-1" />
                    {t("carDetails.instantBook")}
                  </Badge>
                )}
              </div>
            </div>

            {/* Title & Location */}
            <div className="card-base">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave">
                    {vehicle.make} {vehicle.model} {vehicle.year}
                  </h1>
                  <div className="flex items-center gap-2 mt-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>{vehicle.location_address}, {vehicle.location_city}</span>
                  </div>
                </div>
                
                {/* Separate Vehicle + Host ratings */}
                <div className="flex flex-wrap items-center gap-2">
                  {vehicleReviews.average != null && (
                    <div
                      className="flex items-center gap-1 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full"
                      data-testid="vehicle-rating-badge"
                      title={t("carDetails.vehicleRatingTitle")}
                    >
                      <Car className="w-4 h-4 text-amber-700" />
                      <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                      <span className="font-semibold text-sm">
                        {vehicleReviews.average.toFixed(1)}
                      </span>
                      <span className="text-amber-700 text-xs">({vehicleReviews.total})</span>
                    </div>
                  )}
                  {hostReviews.average != null && (
                    <div
                      className="flex items-center gap-1 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full"
                      data-testid="host-rating-badge"
                      title={t("carDetails.hostRatingTitle")}
                    >
                      <Users className="w-4 h-4 text-blue-700" />
                      <Star className="w-4 h-4 fill-blue-500 text-blue-500" />
                      <span className="font-semibold text-sm">
                        {hostReviews.average.toFixed(1)}
                      </span>
                      <span className="text-blue-700 text-xs">({hostReviews.total})</span>
                    </div>
                  )}
                  {vehicleReviews.average == null && hostReviews.average == null && vehicle.review_count > 0 && (
                    <div className="flex items-center gap-1 bg-sand px-3 py-1 rounded-full">
                      <Star className="w-4 h-4 fill-marigold text-marigold" />
                      <span className="font-semibold">{vehicle.rating?.toFixed(1)}</span>
                      <span className="text-gray-500 text-sm">({vehicle.review_count})</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-terracotta" />
                  <span>{t("carDetails.seats", { count: vehicle.seats })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Settings2 className="w-5 h-5 text-terracotta" />
                  <span>{vehicle.transmission === "automatic" ? t("searchResults.automatic") : t("searchResults.manual")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Fuel className="w-5 h-5 text-terracotta" />
                  <span className="capitalize">{vehicle.fuel_type}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Car className="w-5 h-5 text-terracotta" />
                  <span>{vehicle.color}</span>
                </div>
              </div>
            </div>

            {/* Description */}
            {vehicle.description && (
              <div className="card-base">
                <h3 className="font-heading font-semibold text-lg mb-3">{t("carDetails.description")}</h3>
                <p className="text-gray-600 leading-relaxed">{vehicle.description}</p>
              </div>
            )}

            {/* Features List */}
            <div className="card-base">
              <h3 className="font-heading font-semibold text-lg mb-4">{t("carDetails.features")}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {vehicle.features?.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Host Info */}
            <div className="card-base">
              <h3 className="font-heading font-semibold text-lg mb-4">{t("carDetails.yourHost")}</h3>
              {vehicle.host?.user_id ? (
                <Link
                  to={`/u/${vehicle.host.user_id}`}
                  data-testid="host-profile-link"
                  className="group flex items-center gap-4 -m-2 p-2 rounded-2xl hover:bg-neutral-50 transition-colors"
                >
                  <Avatar className="w-16 h-16 ring-1 ring-neutral-200 group-hover:ring-terracotta/40 transition">
                    <AvatarImage src={vehicle.host?.picture} />
                    <AvatarFallback className="bg-terracotta text-white text-xl">
                      {vehicle.host?.name?.charAt(0) || "H"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-lg group-hover:text-terracotta transition-colors truncate">
                      {vehicle.host?.name}
                    </p>
                    <p className="text-gray-500 text-sm">
                      {t("carDetails.tripsCompleted", { count: vehicle.trips_count })} · {t("carDetails.viewProfile")}
                    </p>
                  </div>
                </Link>
              ) : (
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={vehicle.host?.picture} />
                    <AvatarFallback className="bg-terracotta text-white text-xl">
                      {vehicle.host?.name?.charAt(0) || "H"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-lg">{vehicle.host?.name}</p>
                    <p className="text-gray-500 text-sm">{t("carDetails.tripsCompleted", { count: vehicle.trips_count })}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Reviews */}
            {(vehicleReviews.reviews?.length > 0 || vehicleReviews.total > 0) && (
              <div className="card-base">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <h3 className="font-heading font-semibold text-lg">
                    {t("carDetails.reviews")} ({vehicleReviews.total})
                  </h3>
                  {vehicleReviews.average != null && (
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                      <span className="font-semibold">{vehicleReviews.average.toFixed(1)}</span>
                      <span className="text-gray-500">/ 5</span>
                    </div>
                  )}
                </div>

                {/* Criteria breakdown bars */}
                {Object.keys(vehicleReviews.breakdown || {}).length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-6 pb-6 border-b">
                    {[
                      { key: "cleanliness", label: t("carDetails.cleanliness") },
                      { key: "mechanical", label: t("carDetails.mechanical") },
                      { key: "comfort", label: t("carDetails.comfort") },
                      { key: "accuracy", label: t("carDetails.accuracy") },
                      { key: "overall", label: t("carDetails.overallQuality") },
                    ]
                      .filter((c) => vehicleReviews.breakdown[c.key] != null)
                      .map((c) => (
                        <div key={c.key} className="flex items-center gap-3 text-sm">
                          <span className="w-32 text-neutral-600">{c.label}</span>
                          <div className="flex-1 bg-neutral-100 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="h-1.5 bg-amber-400"
                              style={{ width: `${(vehicleReviews.breakdown[c.key] / 5) * 100}%` }}
                            />
                          </div>
                          <span className="font-semibold w-8 text-right">
                            {vehicleReviews.breakdown[c.key].toFixed(1)}
                          </span>
                        </div>
                      ))}
                  </div>
                )}

                <div className="space-y-4">
                  {vehicleReviews.reviews.map((review, index) => {
                    const reviewerId = review.reviewer?.user_id || review.reviewer_id;
                    const HeaderTag = reviewerId ? Link : "div";
                    const headerProps = reviewerId
                      ? {
                          to: `/u/${reviewerId}`,
                          "data-testid": `reviewer-link-${index}`,
                          className:
                            "group flex items-center gap-3 mb-2 -m-1 p-1 rounded-lg hover:bg-neutral-50 transition-colors",
                        }
                      : { className: "flex items-center gap-3 mb-2" };
                    return (
                      <div key={review.review_id || index}>
                        <HeaderTag {...headerProps}>
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={review.reviewer?.picture} />
                            <AvatarFallback className="bg-sand">
                              {review.reviewer?.name?.charAt(0) || "U"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className={`font-medium ${reviewerId ? "group-hover:text-terracotta transition-colors" : ""}`}>
                              {review.reviewer?.name}
                            </p>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-3 h-3 ${
                                    i < (review.vehicle_rating || review.rating)
                                      ? "fill-amber-500 text-amber-500"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                        </HeaderTag>
                        {review.comment && <p className="text-gray-600 text-sm">{review.comment}</p>}
                        {index < vehicleReviews.reviews.length - 1 && <Separator className="mt-4" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <div className="card-base sticky top-24">
              {/* Price Header */}
              <div className="flex items-baseline gap-2 mb-5">
                <span className="text-3xl font-heading font-bold text-terracotta">
                  ${vehicle.daily_rate?.toLocaleString()}
                </span>
                <span className="text-gray-500">{t("carCard.perDay")}</span>
                <UsdHint amountMxn={vehicle.daily_rate} />
              </div>

              <TripScheduler
                vehicleId={vehicleId}
                dailyRate={vehicle.daily_rate}
                weeklyDiscountPct={vehicle.weekly_discount}
                monthlyDiscountPct={vehicle.monthly_discount}
                locationLabel={vehicle.location_address || vehicle.location_city}
                minHours={1}
                onChange={setTripSelection}
                onContinue={handleBookNow}
              />

              {/* Contextual cancellation snippet */}
              {(() => {
                const pk = tripSelection?.pickup;
                const isFarBooking = pk
                  ? (pk.getTime() - Date.now()) / (1000 * 60 * 60) > 24
                  : true; // default to "24h before" wording when no date selected
                return (
                  <div
                    className="mt-5 p-4 rounded-xl border border-emerald-200 bg-emerald-50/60"
                    data-testid="details-cancel-snippet"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="inline-flex w-9 h-9 rounded-lg bg-emerald-100 items-center justify-center shrink-0"
                        aria-hidden
                      >
                        <Shield className="w-4 h-4 text-emerald-700" strokeWidth={2.2} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-emerald-900 leading-snug">
                          {isFarBooking
                            ? t("carDetails.cancelFree24Title")
                            : t("carDetails.cancelFree1hTitle")}
                        </p>
                        <p className="text-xs text-emerald-800/80 mt-1 leading-relaxed">
                          {isFarBooking
                            ? t("carDetails.cancelFree24Body")
                            : t("carDetails.cancelFree1hBody")}
                        </p>
                        <Link
                          to="/cancelacion"
                          target="_blank"
                          rel="noopener"
                          data-testid="details-cancel-policy-link"
                          className="inline-block text-xs font-semibold text-emerald-700 hover:text-emerald-800 mt-1.5 hover:underline"
                        >
                          {t("carDetails.viewFullPolicy")}
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="mt-5 space-y-3 pt-5 border-t border-neutral-100">
                {/* Mileage */}
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  {vehicle.unlimited_mileage ? (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      {t("carDetails.unlimitedIncluded")}
                    </>
                  ) : (
                    <span>
                      {t("carDetails.mileageLimit", { limit: vehicle.mileage_limit_km, fee: vehicle.extra_mileage_fee })}
                    </span>
                  )}
                </p>
                {/* Delivery */}
                {vehicle.delivery_enabled && (
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    {t("carDetails.deliveryAvailable", { km: vehicle.delivery_radius_km, fee: vehicle.delivery_fee })}
                  </p>
                )}
                {/* Protection */}
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  {t("carDetails.protectionPlans")}
                </p>
              </div>

              <p className="text-center text-xs text-gray-500 mt-4">
                {t("carDetails.notChargedYet")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Book Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E0D8] p-4 md:hidden">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xl font-bold text-terracotta">
              ${vehicle.daily_rate?.toLocaleString()}
            </span>
            <span className="text-gray-500 text-sm">{t("carDetails.perDayShort")}</span>
          </div>
          <Button
            onClick={handleBookNow}
            className="btn-primary"
            data-testid="mobile-book-btn"
          >
            {t("carDetails.reserve")}
          </Button>
        </div>
      </div>
    </div>
  );
}
